import i18n from 'config/i18n'
import { TFunction } from 'i18next'
import { EngagementId } from 'state/tasks'
import { BANNER_OPTION_TYPE } from 'state/constants'

const engagementFieldType = [
    EngagementId.VIEW,
    EngagementId.LIKE,
    EngagementId.REACH,
    EngagementId.IMPRESSION,
    EngagementId.COMMENT,
    EngagementId.TAG,
    EngagementId.SHARE,
    EngagementId.STICKER_TAP,
    EngagementId.LINK_CLICK,
    EngagementId.CTA_CLICK,
    EngagementId.SAVE,
    EngagementId.REPLY,
    EngagementId.RETWEET,
    EngagementId.ENGAGEMENT,
    EngagementId.ENGAGEMENT_RATE,
    EngagementId.CONTENT_ENGAGEMENT_RATE,
    EngagementId.TOTAL_ENGAGEMENT,
    EngagementId.TOTAL_WATCH_TIME,
    EngagementId.TOTAL_PLAY_TIME,
    EngagementId.WATCHED_FULL_VIDEO,
    EngagementId.AVERAGE_WATCH_TIME,
    'view_video',
] as const
type ENGAGEMENT_FIELD_TYPE = (typeof engagementFieldType)[number]

export const getEngagementTranslation: Record<string, (t: TFunction) => string> = {
    [EngagementId.VIEW]: t => t('engagementMetric.view'),
    [EngagementId.LIKE]: t => t('engagementMetric.like'),
    [EngagementId.REACH]: t => t('engagementMetric.reach'),
    [EngagementId.IMPRESSION]: t => t('engagementMetric.impression'),
    [EngagementId.COMMENT]: t => t('engagementMetric.numberComments'),
    [EngagementId.TAG]: t => t('engagementMetric.tag'),
    [EngagementId.SHARE]: t => t('engagementMetric.share'),
    [EngagementId.STICKER_TAP]: t => t('engagementMetric.stickerTap'),
    [EngagementId.LINK_CLICK]: t => t('engagementMetric.linkClick'),
    [EngagementId.CTA_CLICK]: t => t('engagementMetric.ctaButtonClicksBoostedPosts'),
    [EngagementId.SAVE]: t => t('engagementMetric.save'),
    [EngagementId.REPLY]: t => t('engagementMetric.reply'),
    [EngagementId.RETWEET]: t => t('engagementMetric.retweet'),
    [EngagementId.ENGAGEMENT]: t => t('engagementMetric.engagement'),
    [EngagementId.CONTENT_ENGAGEMENT_RATE]: t => t('submission.table.contentEngagementRateLabel'),
    [EngagementId.TOTAL_ENGAGEMENT]: t => t('submission.campaignReport.metric.totalEngagement'),
    [EngagementId.ENGAGEMENT_RATE]: t => t('submission.table.engagementRateLabel'),
    [EngagementId.TOTAL_WATCH_TIME]: t => t('engagementMetric.totalWatchTime'),
    [EngagementId.TOTAL_PLAY_TIME]: t => t('submission.campaignReport.metric.totalPlayTime'),
    [EngagementId.WATCHED_FULL_VIDEO]: t => t('engagementMetric.watchedFullVideo'),
    [EngagementId.AVERAGE_WATCH_TIME]: t => t('engagementMetric.averageWatchTime'),
    ['view_video']: t => t('engagementMetric.videoView'),
}

interface EngagementPayoutTranslation {
    perLabel: string
    minLabel: string
    maxLabel: string
}

export const getEngagementPayoutTranslation: Partial<
    Record<ENGAGEMENT_FIELD_TYPE, (t: TFunction) => EngagementPayoutTranslation>
> = {
    [EngagementId.LIKE]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerLike'),
        minLabel: t('engagementMetric.enterMinimumLike'),
        maxLabel: t('engagementMetric.enterMaximumLike'),
    }),
    [EngagementId.COMMENT]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerComment'),
        minLabel: t('engagementMetric.enterMinimumComment'),
        maxLabel: t('engagementMetric.enterMaximumComment'),
    }),
    ['view_video']: t => ({
        perLabel: t('engagementMetric.enterPayoutPerView'),
        minLabel: t('engagementMetric.enterMinimumView'),
        maxLabel: t('engagementMetric.enterMaximumView'),
    }),
    [EngagementId.SHARE]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerShare'),
        minLabel: t('engagementMetric.enterMinimumShare'),
        maxLabel: t('engagementMetric.enterMaximumShare'),
    }),
    [EngagementId.TAG]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerTag'),
        minLabel: t('engagementMetric.enterMinimumTag'),
        maxLabel: t('engagementMetric.enterMaximumTag'),
    }),
    [EngagementId.REACH]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerReach'),
        minLabel: t('engagementMetric.enterMinimumReach'),
        maxLabel: t('engagementMetric.enterMaximumReach'),
    }),
    [EngagementId.IMPRESSION]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerImpression'),
        minLabel: t('engagementMetric.enterMinimumImpression'),
        maxLabel: t('engagementMetric.enterMaximumImpression'),
    }),
    [EngagementId.VIEW]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerView'),
        minLabel: t('engagementMetric.enterMinimumView'),
        maxLabel: t('engagementMetric.enterMaximumView'),
    }),
    [EngagementId.STICKER_TAP]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerStickerTap'),
        minLabel: t('engagementMetric.enterMinimumStickerTap'),
        maxLabel: t('engagementMetric.enterMaximumStickerTap'),
    }),
    [EngagementId.SAVE]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerSave'),
        minLabel: t('engagementMetric.enterMinimumSave'),
        maxLabel: t('engagementMetric.enterMaximumSave'),
    }),
    [EngagementId.CTA_CLICK]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerCtaButtonClick'),
        minLabel: t('engagementMetric.enterMinimumCtaButtonClick'),
        maxLabel: t('engagementMetric.enterMaximumCtaButtonClick'),
    }),
    [EngagementId.LINK_CLICK]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerLinkClick'),
        minLabel: t('engagementMetric.enterMinimumLinkClick'),
        maxLabel: t('engagementMetric.enterMaximumLinkClick'),
    }),
    [EngagementId.REPLY]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerReply'),
        minLabel: t('engagementMetric.enterMinimumReply'),
        maxLabel: t('engagementMetric.enterMaximumReply'),
    }),
    [EngagementId.RETWEET]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerRetweet'),
        minLabel: t('engagementMetric.enterMinimumRetweet'),
        maxLabel: t('engagementMetric.enterMaximumRetweet'),
    }),
    [EngagementId.ENGAGEMENT]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerEngagement'),
        minLabel: t('engagementMetric.enterMinimumEngagement'),
        maxLabel: t('engagementMetric.enterMaximumEngagement'),
    }),
    [EngagementId.TOTAL_WATCH_TIME]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerTotalWatchTime'),
        minLabel: t('engagementMetric.enterPayoutMinimumTotalWatchTime'),
        maxLabel: t('engagementMetric.enterPayoutMaximumTotalWatchTime'),
    }),
    [EngagementId.TOTAL_PLAY_TIME]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerTotalPlayTime'),
        minLabel: t('engagementMetric.enterPayoutMinimumTotalPlayTime'),
        maxLabel: t('engagementMetric.enterPayoutMaximumTotalPlayTime'),
    }),
    [EngagementId.WATCHED_FULL_VIDEO]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerWatchedFullVideo'),
        minLabel: t('engagementMetric.enterPayoutMinimumWatchedFullVideo'),
        maxLabel: t('engagementMetric.enterPayoutMaximumWatchedFullVideo'),
    }),
    [EngagementId.AVERAGE_WATCH_TIME]: t => ({
        perLabel: t('engagementMetric.enterPayoutPerAverageWatchTime'),
        minLabel: t('engagementMetric.enterPayoutMinimumAverageWatchTime'),
        maxLabel: t('engagementMetric.enterPayoutMaximumAverageWatchTime'),
    }),
}

const engagementHeaderFieldType = [
    EngagementId.VIEW,
    EngagementId.LIKE,
    EngagementId.REACH,
    EngagementId.IMPRESSION,
    EngagementId.COMMENT,
    EngagementId.TAG,
    EngagementId.SHARE,
    EngagementId.STICKER_TAP,
    EngagementId.LINK_CLICK,
    EngagementId.CTA_CLICK,
    EngagementId.SAVE,
    EngagementId.REPLY,
    EngagementId.RETWEET,
    EngagementId.ENGAGEMENT,
    EngagementId.ENGAGEMENT_RATE,
    EngagementId.CONTENT_ENGAGEMENT_RATE,
    EngagementId.TOTAL_ENGAGEMENT,
    EngagementId.TOTAL_WATCH_TIME,
    EngagementId.TOTAL_PLAY_TIME,
    EngagementId.WATCHED_FULL_VIDEO,
    EngagementId.AVERAGE_WATCH_TIME,
    'follower_count',
] as const
type ENGAGEMENT_HEADER_FIELD_TYPE = (typeof engagementHeaderFieldType)[number]

export const getEngagementHeaderTranslation: Record<ENGAGEMENT_HEADER_FIELD_TYPE, () => string> = {
    [EngagementId.VIEW]: () => i18n.t('submission.table.viewsLabel'),
    [EngagementId.LIKE]: () => i18n.t('submission.table.likesLabel'),
    [EngagementId.REACH]: () => i18n.t('submission.table.reachLabel'),
    [EngagementId.IMPRESSION]: () => i18n.t('submission.table.impressionLabel'),
    [EngagementId.COMMENT]: () => i18n.t('submission.table.commentsLabel'),
    [EngagementId.TAG]: () => i18n.t('submission.table.tagsDateLabel'),
    [EngagementId.SHARE]: () => i18n.t('submission.table.sharesLabel'),
    [EngagementId.STICKER_TAP]: () => i18n.t('submission.table.stickerTapsLabel'),
    [EngagementId.LINK_CLICK]: () => i18n.t('submission.table.linkClicksLabel'),
    [EngagementId.CTA_CLICK]: () => i18n.t('submission.table.ctaButtonClicksLabel'),
    [EngagementId.SAVE]: () => i18n.t('submission.table.savesLabel'),
    [EngagementId.REPLY]: () => i18n.t('submission.table.repliesLabel'),
    [EngagementId.RETWEET]: () => i18n.t('submission.table.retweetsLabel'),
    [EngagementId.ENGAGEMENT]: () => i18n.t('submission.table.engagementLabel'),
    [EngagementId.CONTENT_ENGAGEMENT_RATE]: () => i18n.t('submission.table.contentEngagementRateLabel'),
    [EngagementId.TOTAL_ENGAGEMENT]: () => i18n.t('submission.campaignReport.metric.totalEngagement'),
    [EngagementId.ENGAGEMENT_RATE]: () => i18n.t('submission.table.engagementRateLabel'),
    // Update copy keys when ready
    [EngagementId.TOTAL_WATCH_TIME]: () => i18n.t('engagementMetric.totalWatchTime'),
    [EngagementId.TOTAL_PLAY_TIME]: () => i18n.t('submission.campaignReport.metric.totalPlayTime'),
    [EngagementId.WATCHED_FULL_VIDEO]: () => i18n.t('engagementMetric.watchedFullVideo'),
    [EngagementId.AVERAGE_WATCH_TIME]: () => i18n.t('engagementMetric.averageWatchTime'),
    ['follower_count']: () => i18n.t('submission.table.followerCountLabel'),
}

export const bannerOption = {
    campaign: 'Campaign',
    profile: 'Profile',
    submission: 'Submission',
    poll: 'Poll',
    task: 'Task',
    url: 'Url',
} as const

export const getBannerRedirectLinkOptionTranslation: Record<BANNER_OPTION_TYPE, (t: TFunction) => string> = {
    [bannerOption.campaign]: t => t('banner.selectBannerRedirectLinkCampaignBriefMenuOption'),
    [bannerOption.profile]: t => t('banner.selectBannerRedirectLinkPartiposterProfileMenuOption'),
    [bannerOption.submission]: t => t('banner.selectBannerRedirectLinkPartiposterSubmissionMenuOption'),
    [bannerOption.poll]: t => t('banner.selectBannerRedirectLinkPollsFeedMenuOption'),
    [bannerOption.task]: t => t('banner.selectBannerRedirectLinkCampaignTasksMenuOption'),
    [bannerOption.url]: t => t('banner.selectBannerRedirectLinkUrlOption'),
}

export const tagCategoryOption = {
    submission_edits_caption: 'submission_edits_caption',
    submission_edits_content_quality: 'submission_edits_content_quality',
    submission_edits_product: 'submission_edits_product',
    submission_edits_media: 'submission_edits_media',
    submission_edits_others: 'submission_edits_others',
} as const

export const getTagCategoryTranslation: Record<string, (t: TFunction) => string> = {
    [tagCategoryOption.submission_edits_content_quality]: t =>
        t('submission.review.contentQualityEditRequestTagMenuCategory'),
    [tagCategoryOption.submission_edits_caption]: t => t('submission.review.captionEditRequestTagMenuCategory'),
    [tagCategoryOption.submission_edits_others]: t => t('submission.review.othersEditRequestTagMenuCategory'),
    [tagCategoryOption.submission_edits_media]: t => t('submission.review.photoVideoEditRequestTagMenuCategory'),
    [tagCategoryOption.submission_edits_product]: t => t('submission.review.brandProductEditRequestTagMenuCategory'),
}

export const tagCategoryRejectionsOption = {
    submission_rejections_profile_feed: 'submission_rejections_profile_feed',
    submission_rejections_followers: 'submission_rejections_followers',
    submission_rejections_age: 'submission_rejections_age',
    submission_rejections_gender: 'submission_rejections_gender',
    submission_rejections_others: 'submission_rejections_others',
} as const

export const getTagCategoryRejectionsTranslation: Record<string, (t: TFunction) => string> = {
    [tagCategoryRejectionsOption.submission_rejections_profile_feed]: t =>
        t('submission.review.profileFeedRejectTagMenuCategory'),
    [tagCategoryRejectionsOption.submission_rejections_followers]: t =>
        t('submission.review.audienceFollowersRejectTagMenuCategory'),
    [tagCategoryRejectionsOption.submission_rejections_age]: t => t('submission.review.ageRejectTagMenuCategory'),
    [tagCategoryRejectionsOption.submission_rejections_gender]: t => t('submission.review.genderRejectTagMenuCategory'),
    [tagCategoryRejectionsOption.submission_rejections_others]: t => t('submission.review.othersTagMenuCategory'),
}

export const SubmissionTagOption = {
    caption_missing_requirements: 'caption_missing_requirements',
    caption_tone_more_organic: 'caption_tone_more_organic',
    caption_tone_more_positive: 'caption_tone_more_positive',
    content_quality_too_dark: 'content_quality_too_dark',
    content_quality_too_bright: 'content_quality_too_bright',
    content_quality_too_grainy_blurry: 'content_quality_too_grainy_blurry',
    content_quality_too_filtered: 'content_quality_too_filtered',
    content_quality_too_saturated: 'content_quality_too_saturated',
    product_too_small: 'product_too_small',
    product_too_big: 'product_too_big',
    product_not_visible: 'product_not_visible',
    product_logo_not_visible: 'product_logo_not_visible',
    product_inverted_logo: 'product_inverted_logo',
    product_inverted_brand_name: 'product_inverted_brand_name',
    photo_video_missing_requirements: 'photo_video_missing_requirements',
    video_audio_not_suitable: 'video_audio_not_suitable',
    photo_video_text_should_not_be_there: 'photo_video_text_should_not_be_there',
    video_mismatch_audio_and_video: 'video_mismatch_audio_and_video',
    frozen_video: 'frozen_video',
    submission_edits_others_other_reason: 'submission_edits_others_other_reason',
} as const

export const getSubmissionTagReviewTranslation: Record<string, (t: TFunction) => string> = {
    [SubmissionTagOption.caption_missing_requirements]: t =>
        t('submission.review.captionRequirementsEditRequestComment'),
    [SubmissionTagOption.caption_tone_more_organic]: t => t('submission.review.toneOrganicEditRequestComment'),
    [SubmissionTagOption.caption_tone_more_positive]: t => t('submission.review.tonePositiveEditRequestComment'),
    [SubmissionTagOption.content_quality_too_dark]: t => t('submission.review.tooDarkEditRequestComment'),
    [SubmissionTagOption.content_quality_too_bright]: t => t('submission.review.tooBrightEditRequestComment'),
    [SubmissionTagOption.content_quality_too_grainy_blurry]: t => t('submission.review.tooGrainyEditRequestComment'),
    [SubmissionTagOption.content_quality_too_filtered]: t => t('submission.review.tooFilteredEditRequestComment'),
    [SubmissionTagOption.content_quality_too_saturated]: t => t('submission.review.tooSaturatedEditRequestComment'),
    [SubmissionTagOption.product_too_small]: t => t('submission.review.productTooSmallEditRequestComment'),
    [SubmissionTagOption.product_too_big]: t => t('submission.review.productTooBigEditRequestComment'),
    [SubmissionTagOption.product_not_visible]: t => t('submission.review.productNotVisibleEditRequestComment'),
    [SubmissionTagOption.product_logo_not_visible]: t => t('submission.review.logoNotVisibleEditRequestComment'),
    [SubmissionTagOption.product_inverted_logo]: t => t('submission.review.invertedLogoEditRequestComment'),
    [SubmissionTagOption.product_inverted_brand_name]: t => t('submission.review.invertedBrandNameEditRequestComment'),
    [SubmissionTagOption.photo_video_missing_requirements]: t =>
        t('submission.review.photoRequirementsEditRequestComment'),
    [SubmissionTagOption.video_audio_not_suitable]: t => t('submission.review.videoNotSuitableEditRequestComment'),
    [SubmissionTagOption.photo_video_text_should_not_be_there]: t =>
        t('submission.review.textPlacementEditRequestComment'),
    [SubmissionTagOption.video_mismatch_audio_and_video]: t => t('submission.review.audioMismatchEditRequestComment'),
    [SubmissionTagOption.frozen_video]: t => t('submission.review.frozenVideoEditRequestComment'),
    [SubmissionTagOption.submission_edits_others_other_reason]: t => t('submission.review.othersReasonsComment'),
}

export const getSubmissionTagTranslation: Record<string, (t: TFunction) => string> = {
    [SubmissionTagOption.caption_missing_requirements]: t => t('tag.submission.captionRequirements'),
    [SubmissionTagOption.caption_tone_more_organic]: t => t('tag.submission.toneOrganic'),
    [SubmissionTagOption.caption_tone_more_positive]: t => t('tag.submission.tonePositive'),
    [SubmissionTagOption.content_quality_too_dark]: t => t('tag.submission.tooDark'),
    [SubmissionTagOption.content_quality_too_bright]: t => t('tag.submission.tooBright'),
    [SubmissionTagOption.content_quality_too_grainy_blurry]: t => t('tag.submission.tooGrainy'),
    [SubmissionTagOption.content_quality_too_filtered]: t => t('tag.submission.tooFiltered'),
    [SubmissionTagOption.content_quality_too_saturated]: t => t('tag.submission.tooSaturated'),
    [SubmissionTagOption.product_too_small]: t => t('tag.submission.productTooSmall'),
    [SubmissionTagOption.product_too_big]: t => t('tag.submission.productTooBig'),
    [SubmissionTagOption.product_not_visible]: t => t('tag.submission.productNotVisible'),
    [SubmissionTagOption.product_logo_not_visible]: t => t('tag.submission.logoNotVisible'),
    [SubmissionTagOption.product_inverted_logo]: t => t('tag.submission.invertedLogo'),
    [SubmissionTagOption.product_inverted_brand_name]: t => t('tag.submission.invertedBrandName'),
    [SubmissionTagOption.photo_video_missing_requirements]: t => t('tag.submission.photoRequirements'),
    [SubmissionTagOption.video_audio_not_suitable]: t => t('tag.submission.videoNotSuitable'),
    [SubmissionTagOption.photo_video_text_should_not_be_there]: t => t('tag.submission.textPlacement'),
    [SubmissionTagOption.video_mismatch_audio_and_video]: t => t('tag.submission.mismatchAudio'),
    [SubmissionTagOption.frozen_video]: t => t('tag.submission.frozenVideo'),
    [SubmissionTagOption.submission_edits_others_other_reason]: t => t('tag.submission.otherReasons'),
}

export const SubmissionRejectionOption = {
    profile_feed_too_sexy: 'profile_feed_too_sexy',
    profile_feed_too_serious: 'profile_feed_too_serious',
    profile_feed_too_bright: 'profile_feed_too_bright',
    profile_feed_too_dark: 'profile_feed_too_dark',
    followers_too_low: 'followers_too_low',
    followers_too_high: 'followers_too_high',
    followers_too_fake: 'followers_too_fake',
    age_too_young: 'age_too_young',
    age_too_old: 'age_too_old',
    gender_not_female: 'gender_not_female',
    gender_not_male: 'gender_not_male',
    submission_rejections_others_other_reason: 'submission_rejections_others_other_reason',
} as const

export const getSubmissionRejectionsTranslation: Record<string, (t: TFunction) => string> = {
    [SubmissionRejectionOption.profile_feed_too_sexy]: t => t('tag.submission.tooSexy'),
    [SubmissionRejectionOption.profile_feed_too_serious]: t => t('tag.submission.tooSerious'),
    [SubmissionRejectionOption.profile_feed_too_bright]: t => t('tag.submission.tooBright'),
    [SubmissionRejectionOption.profile_feed_too_dark]: t => t('tag.submission.tooDark'),
    [SubmissionRejectionOption.followers_too_low]: t => t('tag.submission.followersLow'),
    [SubmissionRejectionOption.followers_too_high]: t => t('tag.submission.followersHigh'),
    [SubmissionRejectionOption.followers_too_fake]: t => t('tag.submission.followersFake'),
    [SubmissionRejectionOption.age_too_young]: t => t('tag.submission.tooYoung'),
    [SubmissionRejectionOption.age_too_old]: t => t('tag.submission.tooOld'),
    [SubmissionRejectionOption.gender_not_female]: t => t('tag.submission.notFemale'),
    [SubmissionRejectionOption.gender_not_male]: t => t('tag.submission.notMale'),
    [SubmissionRejectionOption.submission_rejections_others_other_reason]: t => t('tag.submission.otherReasons'),
}

export const getSubmissionRejectionsReviewTranslation: Record<string, (t: TFunction) => string> = {
    [SubmissionRejectionOption.profile_feed_too_sexy]: t => t('submission.review.tooSexyRejectComment'),
    [SubmissionRejectionOption.profile_feed_too_serious]: t => t('submission.review.tooSeriousRejectComment'),
    [SubmissionRejectionOption.profile_feed_too_bright]: t => t('submission.review.tooBrightRejectComment'),
    [SubmissionRejectionOption.profile_feed_too_dark]: t => t('submission.review.tooDarkRejectComment'),
    [SubmissionRejectionOption.followers_too_low]: t => t('submission.review.followersLowRejectComment'),
    [SubmissionRejectionOption.followers_too_high]: t => t('submission.review.followersHighRejectComment'),
    [SubmissionRejectionOption.followers_too_fake]: t => t('submission.review.followersFakeRejectComment'),
    [SubmissionRejectionOption.age_too_young]: t => t('submission.review.tooYoungRejectComment'),
    [SubmissionRejectionOption.age_too_old]: t => t('submission.review.tooOldRejectComment'),
    [SubmissionRejectionOption.gender_not_female]: t => t('submission.review.notFemaleRejectComment'),
    [SubmissionRejectionOption.gender_not_male]: t => t('submission.review.notMaleRejectComment'),
    [SubmissionRejectionOption.submission_rejections_others_other_reason]: t =>
        t('submission.review.othersReasonsComment'),
}

export const getScheduleSubtextTranslation: Record<string, (t: TFunction) => string> = {
    purchase_0: t => t('campaign.template.step2.purchaseTask.step1.subText'),
    purchase_1: t => t('campaign.template.purchaseTask.step2.subText'),
    ig_story_repost_0: t => t('campaign.template.instagramStoryTask.step1.subText'),
    ig_story_repost_1: t => t('campaign.template.instagramStoryTask.step2.subText'),
    ig_story_repost_2: t => t('campaign.template.instagramStoryTask.step3.subText'),
    ig_reels_0: t => t('campaign.template.instagramReelTask.step1.subText'),
    ig_reels_1: t => t('campaign.template.instagramReelTask.step2.subText'),
    ig_reels_2: t => t('campaign.template.instagramReelTask.step3.subText'),
    tiktok_repost_0: t => t('campaign.template.tiktokRepostTask.step1.subText'),
    tiktok_repost_1: t => t('campaign.template.tiktokRepostTask.step2.subText'),
    download_app_sign_up_without_id_0: t => t('campaign.template.step2.downloadRegisterAppTask.step1.subText'),
    download_add_0: t => t('campaign.template.step2.downloadAppTask.step1.subText'),
    review_text_image_approval_0: t => t('campaign.template.reviewTextImageWithApprovalTask.step1.subText'),
    review_text_image_approval_1: t => t('campaign.template.reviewTextImageWithApprovalTask.step2.subText'),
    review_text_image_approval_purchase_0: t => t('campaign.template.reviewTextImageWithApprovalTask.step1.subText'),
    review_text_image_approval_purchase_1: t => t('campaign.template.reviewTextImageWithApprovalTask.step2.subText'),
    tiktok_0: t => t('campaign.template.tiktokTask.step1.subText'),
    tiktok_1: t => t('campaign.template.tiktokTask.step2.subText'),
    tiktok_2: t => t('campaign.template.tiktokTask.step3.subText'),
    ig_post_0: t => t('campaign.template.igPostTask.step1.subtext'),
    ig_post_1: t => t('campaign.template.igPostTask.step2.subText'),
    ig_post_2: t => t('campaign.template.igPostTask.step3.subText'),
    ig_story_non_ugc_sticker_link_0: t => t('campaign.template.instagramStoryTask.step1.subText'),
    ig_story_non_ugc_sticker_link_1: t => t('campaign.template.instagramStoryTask.step2.subText'),
    ig_story_non_ugc_sticker_link_2: t => t('campaign.template.instagramStoryTask.step3.subText'),
    follow_0: t => t('campaign.template.followTask.step1.subText'),
    follow_1: t => t('campaign.template.followTask.step2.subText'),
}
